import React, {useEffect, useState} from "react";
import moment from "moment";
import {Box, ButtonBase, Fade, IconButton, InputBase, MenuItem, Modal, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {
  controlSx, FieldHelper, FieldLabel, modalCancelSx, ModalButton, modalPaperSx, modalSaveSx, selectSx,
} from "../../../components/modals/formFieldShared";

// datetime-local inputs work with "YYYY-MM-DDTHH:mm"; the backend expects a zone-less date-time
const INPUT_FORMAT = "YYYY-MM-DDTHH:mm";
const PRESETS = [
  {label: "30 days", days: 30},
  {label: "90 days", days: 90},
  {label: "1 year", days: 365},
];

const fromNow = (days) => moment().add(days, "days").startOf("day").format(INPUT_FORMAT);

// When `organizationId` is given the key is always created for that organization
export const ApiKeyDialog = ({open, handleClose, selectedKey, organizations, organizationId: fixedOrganizationId, onSubmit}) => {
  const [validUntil, setValidUntil] = useState("");
  const [organizationId, setOrganizationId] = useState(null);
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(selectedKey?.id);
  const isInFuture = validUntil && moment(validUntil).isAfter(moment());

  useEffect(() => {
    if (!open) return;
    setValidUntil(isEdit ? moment(selectedKey.validUntil).format(INPUT_FORMAT) : fromNow(90));
    setOrganizationId(fixedOrganizationId ?? null);
  }, [selectedKey, open, fixedOrganizationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isInFuture) return;
    setLoading(true);
    const success = await onSubmit({
      validUntil: moment(validUntil).format("YYYY-MM-DDTHH:mm:ss"),
      organizationId,
    });
    setLoading(false);
    if (success) handleClose();
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Fade in={open}>
        <Box component={"form"} onSubmit={handleSubmit} sx={{...modalPaperSx(420), p: {xs: 2.5, md: 3}}}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{mb: 3}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              {isEdit ? "Change Expiry" : "Create API Key"}
            </Typography>
            <IconButton size={"small"} onClick={loading ? undefined : handleClose} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{mb: 3}}>
            {isEdit && (
              <Typography sx={{fontSize: 13, fontFamily: "monospace", color: "#8A8AA0"}} noWrap>
                {selectedKey.maskedKey}
              </Typography>
            )}

            <Box>
              <FieldLabel required>Valid Until</FieldLabel>
              <Box sx={{...controlSx, ...(validUntil && !isInFuture && {boxShadow: "inset 0 0 0 1.5px #DC2626"})}}>
                <InputBase
                  type={"datetime-local"} fullWidth value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  inputProps={{min: moment().format(INPUT_FORMAT), "aria-label": "Valid until"}}
                  sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
              <FieldHelper error={Boolean(validUntil) && !isInFuture}>
                {validUntil && !isInFuture ? "Must be in the future" : "The key stops working after this date"}
              </FieldHelper>
            </Box>

            <Stack direction={"row"} gap={1}>
              {PRESETS.map(preset => {
                const selected = validUntil === fromNow(preset.days);
                return (
                  <ButtonBase
                    key={preset.days}
                    onClick={() => setValidUntil(fromNow(preset.days))}
                    sx={{
                      height: 30, px: 1.25, borderRadius: "999px", fontSize: 12, fontWeight: 600,
                      color: selected ? "#4B55C9" : "#6B6B80", bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "#FAFAFD",
                      "&:hover": {bgcolor: selected ? "rgba(132, 145, 247, 0.18)" : "#ECECF3"},
                    }}
                  >
                    {preset.label}
                  </ButtonBase>
                );
              })}
            </Stack>

            {!isEdit && !fixedOrganizationId && (
              <Box>
                <FieldLabel>Organization</FieldLabel>
                <Select
                  value={organizationId ?? ""}
                  onChange={(e) => setOrganizationId(e.target.value || null)}
                  sx={selectSx}
                  MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                >
                  <MenuItem value={""}>Personal (only me)</MenuItem>
                  {organizations?.map(org => (
                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                  ))}
                </Select>
                <FieldHelper>Members of the organization can also view and manage this key</FieldHelper>
              </Box>
            )}
          </Stack>

          <Stack direction={"row"} gap={1.5} justifyContent={"flex-end"}>
            <ModalButton onClick={loading ? undefined : handleClose} sx={modalCancelSx}>Cancel</ModalButton>
            <ModalButton type={"submit"} disabled={loading || !isInFuture} sx={modalSaveSx}>
              {isEdit ? "Update" : "Create Key"}
            </ModalButton>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};
